import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";

const DessertationCustomTopics = () => {
    const [rotation, setRotation] = useState(0);
    const [layout, setLayout] = useState({
        radius: 160,
        cardSize: "w-32 h-40",
        containerSize: "w-[450px] h-[450px]",
        centerSize: "w-44 h-44",
        imageSize: "w-32 h-32"
    });

    const cards = [
        { id: 1, title: "Personalized Topic Suggestions", icon: <Star className="w-8 h-8 text-white" /> },
        { id: 2, title: "Trend Analysis & Research Gaps ", icon: <Star className="w-8 h-8 text-white" /> },
        { id: 3, title: "Feasibility Assessment", icon: <Star className="w-8 h-8 text-white" /> },
        { id: 4, title: "Refinement & Scope Definition", icon: <Star className="w-8 h-8 text-white" /> },
        { id: 5, title: "Approval Support", icon: <Star className="w-8 h-8 text-white" /> }
    ];

    useEffect(() => {
        const timer = setInterval(() => {
            setRotation((prev) => (prev + 3) % 360);
        }, 100);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const updateLayout = () => {
            const screenWidth = window.innerWidth;
            if (screenWidth < 640) {
                setLayout({
                    radius: 90,
                    cardSize: "w-20 h-28",
                    containerSize: "w-[260px] h-[260px]",
                    centerSize: "w-24 h-24",
                    imageSize: "w-16 h-16"
                });
            } else if (screenWidth < 768) {
                setLayout({
                    radius: 110,
                    cardSize: "w-24 h-32",
                    containerSize: "w-[320px] h-[320px]",
                    centerSize: "w-28 h-28",
                    imageSize: "w-20 h-20"
                });
            } else if (screenWidth < 1024) {
                setLayout({
                    radius: 140,
                    cardSize: "w-28 h-36",
                    containerSize: "w-[380px] h-[380px]",
                    centerSize: "w-36 h-36",
                    imageSize: "w-28 h-28"
                });
            } else {
                setLayout({
                    radius: 160,
                    cardSize: "w-32 h-40",
                    containerSize: "w-[450px] h-[450px]",
                    centerSize: "w-44 h-44",
                    imageSize: "w-32 h-32"
                });
            }
        };

        updateLayout();
        window.addEventListener("resize", updateLayout);
        return () => window.removeEventListener("resize", updateLayout);
    }, []);

    return (
        <div className="flex flex-col lg:flex-row items-center justify-center min-h-screen bg-[#f3f4f6] p-4 md:p-6 overflow-hidden font-['Inter', sans-serif]">
            <div className="relative w-full lg:w-1/2 flex items-center justify-center mb-8 lg:mb-0">
                <div className={`${layout.containerSize} relative rounded-full border-4 border-[#ff9700] flex items-center justify-center`}>
                    <div className="absolute flex items-center justify-center">
                        <div className={`${layout.centerSize} rounded-full border-4 border-dashed border-blue-950 flex items-center justify-center`}>
                            <div className={`${layout.imageSize} rounded-full overflow-hidden border-4 border-white`}>
                                <img
                                    src="logo22.png"
                                    alt="Center Image"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    {cards.map((card, index) => {
                        const angle = ((index * (360 / cards.length)) + rotation) * (Math.PI / 180);
                        const x = Math.cos(angle) * layout.radius;
                        const y = Math.sin(angle) * layout.radius;

                        return (
                            <div
                                key={card.id}
                                className={`${layout.cardSize} absolute rounded-xl shadow-lg bg-[#172554] transition-transform duration-300 hover:scale-105 p-3 flex flex-col items-center justify-center gap-2`}
                                style={{
                                    transform: `translate(${x}px, ${y}px)`,
                                }}
                            >
                                {card.icon}
                                <h3 className="text-center text-white font-bold text-xs sm:text-sm md:text-base tracking-tight">{card.title}</h3>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="mb-12 w-full lg:w-1/2 px-4 md:px-6 max-w-2xl mx-auto">
                <h1 className="text-xl sm:text-2xl font-bold text-center text-blue-950 mb-6">
                    Expert{" "}
                    <span
                        style={{
                            background: "linear-gradient(-96deg, rgb(255 255 255 / 0%) 0%, rgb(255 249 220 / 0%) 28%, rgb(253, 103, 16) 100%, rgb(255 255 255 / 0%) 100%)",
                            padding: "0px 5px",
                        }}
                    >
                        Guidance for Custom
                    </span>
                    Topic Selection
                </h1>
                <div className="space-y-4 text-black p-6">
                    <p className="text-base leading-relaxed text-justify font-medium">
                        Choosing the right dissertation topic is a key step in your research journey. A well-chosen topic creates a strong foundation, ensuring your work is relevant, original, and academically valuable. Many students find it challenging to balance personal interest, research feasibility, and academic contribution.
                    </p>

                    <p className="text-base leading-relaxed text-justify font-medium">
                        Our team helps you identify research-worthy topics tailored to your field. We review current trends, identify research gaps, and suggest options aligned with your career goals. Whether you need help brainstorming ideas or narrowing down a broad subject, we'll ensure you begin your dissertation confidently.
                    </p>
                    <p className="text-base leading-relaxed text-justify font-medium">
                        We go beyond suggestions by evaluating topic feasibility based on available resources, research scope, and academic requirements. Our approach helps you select a topic that's appropriately focused—neither too broad nor too narrow. We also provide guidance on securing approval, refining your proposal, and organizing your initial research.
                    </p>
                    <p className="text-base leading-relaxed text-justify font-medium">
                        With our topic selection service, you gain academic insights, expert mentorship, and a clear roadmap for your dissertation journey. Let us help you take this important first step toward academic success.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default DessertationCustomTopics;